import React, { useMemo } from 'react';
import { useHead } from '@unhead/react';
import { useLocation } from 'react-router-dom';
import { useSEOValidation } from '../../hooks/useSEOValidation';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  type?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  robots?: string;
  structuredData?: object;
  schemas?: object[]; // Array of structured data schemas
}

/**
 * Cleans a canonical URL by removing query parameters, hash fragments, and trailing slashes
 * @param url - The URL to clean
 * @returns Cleaned URL
 */
const cleanCanonicalUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);
    // Remove query params and hash
    urlObj.search = '';
    urlObj.hash = '';
    // Remove trailing slash (except for root path)
    let pathname = urlObj.pathname;
    if (pathname.length > 1 && pathname.endsWith('/')) {
      pathname = pathname.slice(0, -1);
    }
    urlObj.pathname = pathname;
    return urlObj.toString();
  } catch (error) {
    // If URL parsing fails, return as-is
    console.warn('Failed to parse canonical URL:', url, error);
    return url;
  }
};

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  canonical,
  image = '/images/default-og.jpg',
  type = 'website',
  author = 'Horwath HTL',
  publishedTime,
  modifiedTime,
  section,
  robots,
  structuredData,
  schemas
}) => {
  const location = useLocation();
  const siteUrl = 'https://htlmarketcompass.com';
  const fullTitle = `${title} | HTL MarketCompass`;

  // Clean canonical URL
  const canonicalUrl = useMemo(() => {
    const rawUrl = canonical ? `${siteUrl}${canonical}` : `${siteUrl}${location.pathname}`;
    const cleanedUrl = cleanCanonicalUrl(rawUrl);

    // Development mode warnings
    if (process.env.NODE_ENV === 'development' && rawUrl !== cleanedUrl) {
      console.warn(
        `[SEO] Canonical URL was cleaned. Original: "${rawUrl}", Cleaned: "${cleanedUrl}"`
      );
    }

    return cleanedUrl;
  }, [canonical, location.pathname, siteUrl]);

  const imageUrl = image.startsWith('http') ? image : `${siteUrl}${image}`;

  // Run SEO validation in development mode
  useSEOValidation({
    title: fullTitle,
    description,
    canonical: canonicalUrl,
    image: imageUrl,
    structuredData,
    schemas,
  });

  // Build meta tags array
  const metaTags: any[] = [
    { name: 'description', content: description },
    { property: 'og:title', content: fullTitle },
    { property: 'og:description', content: description },
    { property: 'og:type', content: type },
    { property: 'og:url', content: canonicalUrl },
    { property: 'og:image', content: imageUrl },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:site_name', content: 'HTL MarketCompass' },
    { property: 'og:locale', content: 'en_US' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@HorwathHTL' },
    { name: 'twitter:creator', content: '@HorwathHTL' },
    { name: 'twitter:title', content: fullTitle },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: imageUrl },
    { name: 'twitter:image:alt', content: `${title} - HTL MarketCompass` },
  ];

  // Add robots meta if provided
  if (robots) {
    metaTags.push({ name: 'robots', content: robots });
  }

  // Add article-specific OG tags
  if (type === 'article') {
    if (author) metaTags.push({ property: 'article:author', content: author });
    if (publishedTime) metaTags.push({ property: 'article:published_time', content: publishedTime });
    if (modifiedTime) metaTags.push({ property: 'article:modified_time', content: modifiedTime });
    if (section) metaTags.push({ property: 'article:section', content: section });
  }

  // Build script tags array for structured data
  const scriptTags: any[] = [];

  if (structuredData) {
    scriptTags.push({
      type: 'application/ld+json',
      innerHTML: JSON.stringify(structuredData)
    });
  }

  if (schemas) {
    schemas.forEach(schema => {
      scriptTags.push({
        type: 'application/ld+json',
        innerHTML: JSON.stringify(schema)
      });
    });
  }

  // Use @unhead/react
  useHead({
    title: fullTitle,
    meta: metaTags,
    link: [
      { rel: 'canonical', href: canonicalUrl }
    ],
    script: scriptTags.length > 0 ? scriptTags : undefined
  });

  return null;
};

export default SEO;
